import { axiosInterceptor } from "app/services/http-services/request-interceptor";

const STUDIO_AI_REQUEST_TIMEOUT_MS = 600_000;

export type StudioTableMetadata = {
    id: string;
    name: string;
    label: string;
    number_ref?: string | null;
    studio_table: boolean;
};

export type StudioColumnMetadata = {
    id: string;
    sys_table_id?: string | null;
    table: string;
    name: string;
    label: string;
    active?: boolean;
    column_type: string;
    column_subtype?: string | null;
    referenced_table_name?: string | null;
    order?: number | null;
    is_display?: boolean;
    is_hidden?: boolean;
    is_mandatory?: boolean;
    default_value?: string | null;
};

export type StudioContextResponse = {
    package: {
        id: string;
        name: string;
        status: string;
        description?: string | null;
    } | null;
    metadata: {
        tables: StudioTableMetadata[];
        columns: StudioColumnMetadata[];
    };
    studio: {
        ai_providers: any[];
        ai_models: any[];
        ai_profiles: any[];
        ai_tools: any[];
        ai_skills?: any[];
        ai_object_blueprints: any[];
        code_editor_profiles: any[];
        code_editor_profile_rules: any[];
        code_editor_snippets: any[];
        code_editor_libs: any[];
    };
    package_changes: any[];
    architecture: {
        phases: string[];
        condition_ast_example: Record<string, unknown>;
        ai_tool_context?: {
            tools: any[];
            object_blueprints: any[];
            object_docs?: any[];
            object_interactions?: any[];
            object_creation_contract: Record<string, unknown>;
        };
        package_required: boolean;
    };
};

export type StudioArchitectureResponse = {
    session: Record<string, any> | null;
    plan: Record<string, any> | null;
    architecture: {
        phase: string;
        package_id: string;
        requirement_summary: string;
        architecture_summary: string;
        objects: Array<{
            type: string;
            name: string;
            purpose: string;
        }>;
        conditions: any[];
        risks: string[];
        tool_context?: {
            tools: any[];
            object_blueprints: any[];
            object_docs?: any[];
            object_interactions?: any[];
            object_creation_contract: Record<string, unknown>;
        };
    };
};

export type StudioChangeProposal = {
    id: string;
    plan_id: string;
    platform_package_id: string;
    sys_user_id: string;
    operation_type: string;
    target_table: string;
    target_record_id?: string | null;
    target_key: string;
    before_json: Record<string, unknown> | unknown[];
    after_json: Record<string, unknown> | unknown[];
    status: string;
    validation_errors_json: unknown[];
    created_at?: string;
    updated_at?: string;
};

export type StudioProposalResponse = {
    plan: Record<string, any>;
    proposals: StudioChangeProposal[];
    summary: string;
};

export type StudioProposalApplyResponse = {
    applied_count: number;
    proposals: StudioChangeProposal[];
    records: Array<{
        table: string;
        id: string;
        key: string;
    }>;
};

export type StudioAiModel = {
    id: string;
    name: string;
    label?: string | null;
    model_type?: string | null;
    context_window?: number | null;
    max_output_tokens?: number | null;
    cost_input_per_1m?: string | number | null;
    cost_output_per_1m?: string | number | null;
    provider?: {
        id: string;
        name: string;
        label?: string | null;
        provider_type?: string | null;
    } | null;
    access?: Record<string, unknown>;
};

export type StudioSession = {
    id: string;
    title: string;
    state?: string | null;
    created_at?: string;
    updated_at?: string;
    current_context_json?: Record<string, unknown> | null;
};

export type StudioConversationMessage = {
    id: string;
    role: "user" | "assistant";
    content: string;
    architecture_phase?: string | null;
    created_at?: string;
    plan?: Record<string, any> | null;
    architecture?: StudioArchitectureResponse["architecture"] | null;
    proposals?: StudioChangeProposal[];
    proposal_summary?: string | null;
};

export type StudioConversationResponse = {
    session: StudioSession;
    plan: Record<string, any> | null;
    messages: StudioConversationMessage[];
};

export const fetchStudioContext = async (): Promise<StudioContextResponse> => {
    const response = await axiosInterceptor.get("/api/studio/context");
    return response.data.data;
};

export const fetchStudioSessions = async (): Promise<StudioSession[]> => {
    const response = await axiosInterceptor.get("/api/studio/sessions");
    return response.data.data;
};

export const fetchStudioConversation = async (sessionId: string): Promise<StudioConversationResponse> => {
    const response = await axiosInterceptor.get(`/api/studio/sessions/${sessionId}`);
    return response.data.data;
};

export const fetchStudioAiModels = async (actionScope = "architecture"): Promise<StudioAiModel[]> => {
    const response = await axiosInterceptor.get("/api/studio/ai/models", {
        params: { action_scope: actionScope },
        timeout: STUDIO_AI_REQUEST_TIMEOUT_MS,
    });
    return response.data.data;
};

export const requestStudioArchitecture = async (payload: {
    prompt: string;
    session_id?: string | null;
    model_id?: string | null;
    signal?: AbortSignal;
}): Promise<StudioArchitectureResponse> => {
    const { signal, ...body } = payload;
    const response = await axiosInterceptor.post("/api/studio/architecture", body, {
        signal,
        timeout: STUDIO_AI_REQUEST_TIMEOUT_MS,
    });
    return response.data.data;
};

export const generateStudioProposal = async (payload: {
    plan_id: string;
    model_id?: string | null;
    prompt?: string | null;
    signal?: AbortSignal;
}): Promise<StudioProposalResponse> => {
    const { plan_id, signal, ...body } = payload;
    const response = await axiosInterceptor.post(`/api/studio/plans/${plan_id}/proposals`, body, {
        signal,
        timeout: STUDIO_AI_REQUEST_TIMEOUT_MS,
    });
    return response.data.data;
};

export const updateStudioProposalStatus = async (payload: {
    id: string;
    status: "draft" | "approved" | "rejected" | "applied";
}): Promise<StudioChangeProposal> => {
    const response = await axiosInterceptor.patch(`/api/studio/proposals/${payload.id}/status`, {
        status: payload.status,
    });
    return response.data.data;
};

export const applyStudioProposals = async (payload: {
    proposal_ids: string[];
}): Promise<StudioProposalApplyResponse> => {
    const response = await axiosInterceptor.post("/api/studio/proposals/apply", payload, {
        timeout: STUDIO_AI_REQUEST_TIMEOUT_MS,
    });
    return response.data.data;
};
